import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import QrScanner from 'react-qr-scanner';

import aiimsLogo from '../assets/aiims_logo.png';
import http from '../services/http';

// Keep routing inside /t/:slug if present
const tenantBase = () => {
  try {
    const p = window.location.pathname || '';
    const m = p.match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
};

const tenantPath = (p) => {
  const base = tenantBase();
  const clean = p.startsWith('/') ? p : `/${p}`;
  if (!base) return clean;
  if (clean.startsWith('/t/')) return clean;
  return `${base}${clean}`;
};

// Store CMFM session exactly like normal login (prevents auth regressions)
const storeCmfmSession = (data) => {
  const user = data?.user || data?.data?.user || data;
  const token = data?.token || data?.data?.token || user?.token;

  try {
    if (token) {
      localStorage.setItem('cmfm_token', token);
      localStorage.setItem('cmfmToken', token);
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
    }
    localStorage.setItem('loggedInUser', JSON.stringify(user));
    if (user?.role) localStorage.setItem('role', String(user.role).toUpperCase());
  } catch {}
};

const CMFMLoginQR = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();

  // Image upload handler
  const handleImageUpload = async (e) => {
    setMessage('');
    setLoading(true);
    const file = e.target.files?.[0];
    if (!file) {
      setLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = function () {
      const img = new window.Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, canvas.width, canvas.height);

          if (code?.data) {
            await handleQrLogin(code.data);
          } else {
            setMessage('QR Code not recognized / क्यूआर कोड पहचान में नहीं आया');
          }
        } catch {
          setMessage('Image processing error / छवि प्रोसेसिंग में त्रुटि');
        }
        setLoading(false);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Central handler for QR data
  const handleQrLogin = async (userQr) => {
    setLoading(true);
    setMessage('');

    try {
      // ✅ Use shared http client (ensures correct backend base + tenant routing + headers)
      const { data } = await http.post('/api/users/qr-login', { userQr });

      if (data?.user || data?.data?.user) {
        storeCmfmSession(data);

        // ✅ tenant-safe navigation
        navigate(tenantPath('/dashboard'), { replace: true });
      } else {
        setMessage(data?.message || 'Login failed / लॉगिन असफल');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Server error during login / लॉगिन के दौरान सर्वर त्रुटि';
      setMessage(msg);
    }

    setLoading(false);
  };

  // Open scanner with permission check
  const openScanner = async () => {
    setMessage('');
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setShowScanner(true);
    } catch {
      setMessage('Camera permission denied or not available. / कैमरा अनुमति अस्वीकृत या अनुपलब्ध।');
      setShowScanner(false);
    }
  };

  // Camera scan result handler
  const handleScanLegacy = (data) => {
    if (!data) return;
    const scanned = typeof data === 'string' ? data : (data?.text || data?.data || data?.raw || null);
    if (scanned) {
      setShowScanner(false);
      setMessage('');
      handleQrLogin(scanned);
    }
  };

  const handleError = () => {
    setMessage(
      'Unable to access camera. Please allow camera permission and try again. / कैमरा एक्सेस नहीं हो पाया। कृपया अनुमति दें और पुनः प्रयास करें।'
    );
    setShowScanner(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#2d0036] to-[#1a0124] p-2">
      <div className="flex flex-row items-center justify-center w-full mt-6 mb-2 gap-4">
        <div className="bg-white rounded-full shadow-lg flex items-center justify-center" style={{ width: 90, height: 90 }}>
          <img src={aiimsLogo} alt="AIIMS Jodhpur Logo" className="w-16 h-16 object-contain" />
        </div>

        <div className="flex flex-col justify-center items-center">
          <h1 className="text-xl font-bold text-orange-100 leading-tight text-center">
            Department of Community Medicine & Family Medicine
            <br />
            सामुदायिक चिकित्सा एवं पारिवारिक चिकित्सा विभाग
          </h1>
          <h2 className="text-md text-orange-200 mt-1 text-center">AIIMS Jodhpur</h2>
        </div>
      </div>

      <div className="w-full max-w-md bg-white text-black rounded-3xl shadow-xl p-8 mt-1 mb-1 flex flex-col items-center">
        <h2 className="text-2xl font-semibold mb-2 text-center text-purple-800">
          CMFM QR Code Login <br /> सीएमएफएम क्यूआर कोड लॉगिन
        </h2>

        <p className="mb-3 text-sm text-gray-600 text-center">
          Upload a clear photo/screenshot of your QR code
          <br />
          या कैमरा से स्कैन करें
          <br />
          अपना क्यूआर कोड अपलोड करें या कैमरा से स्कैन करें
        </p>

        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="mb-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          disabled={loading}
        />

        <button
          onClick={openScanner}
          className="w-full bg-[#2d0036] text-white font-semibold px-4 py-2 rounded-2xl shadow transition duration-200 mt-1 mb-2"
          disabled={loading}
        >
          Scan with Camera / कैमरा से स्कैन करें
        </button>

        {showScanner && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-80">
            <div className="bg-white rounded-2xl p-4 shadow-lg flex flex-col items-center">
              <p className="font-semibold text-purple-900 mb-2 text-center">
                Point your camera at the CMFM QR Code
                <br />
                अपना क्यूआर कोड कैमरा के सामने रखें
              </p>

              <div style={{ width: 260, height: 260 }}>
                <QrScanner
                  delay={300}
                  style={{ width: '100%', height: '100%' }}
                  onError={handleError}
                  onScan={handleScanLegacy}
                  facingMode="environment"
                />
              </div>

              <button
                onClick={() => setShowScanner(false)}
                className="mt-2 px-4 py-2 rounded-2xl bg-red-700 text-white font-semibold shadow"
              >
                Cancel / रद्द करें
              </button>
            </div>
          </div>
        )}

        {loading && <p className="text-purple-700 mb-2">Processing... कृपया प्रतीक्षा करें...</p>}
        {message && <p className="text-red-600 font-semibold mb-2">{message}</p>}

        <button
          onClick={() => navigate(tenantPath('/cmfm-login'))}
          className="mt-2 px-4 py-2 rounded-2xl bg-[#2d0036] text-white font-semibold shadow hover:bg-purple-900"
          disabled={loading}
        >
          Switch to Password Login / पासवर्ड लॉगिन पर जाएँ
        </button>
      </div>
    </div>
  );
};

export default CMFMLoginQR;
